import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowUpRight, Crown } from "lucide-react";
import { PRICING, MEMBERSHIP, STYLE_FACTORS, ADDONS } from "@/lib/content";
import Estimator from "@/components/site/Estimator";

export default function Pricing({ onQuote }) {
  const navigate = useNavigate();
  const [len, setLen] = useState(24);
  const [styleId, setStyleId] = useState("runabout");
  const [addons, setAddons] = useState([]);

  const toggleAddon = (id) =>
    setAddons((a) => (a.includes(id) ? a.filter((x) => x !== id) : [...a, id]));

  const buildPrefill = (packageName) => {
    const styleLabel = STYLE_FACTORS.find((s) => s.id === styleId)?.label || "";
    const addonLabels = ADDONS.filter((a) => addons.includes(a.id)).map((a) => a.label);
    const parts = [];
    if (packageName) parts.push(packageName);
    if (addonLabels.length) parts.push(`Add-ons: ${addonLabels.join(", ")}`);
    const message = `From estimator — ${len}′ ${styleLabel}${parts.length ? " · " + parts.join(" · ") : ""}.`;
    return { boat_type: `${len}′ ${styleLabel}`, message };
  };

  return (
    <section id="pricing" className="relative bg-[#0A0A0A] py-24 md:py-32 border-t border-white/10" data-testid="pricing-section">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="mb-12 md:mb-16 max-w-2xl"
        >
          <p className="mb-6 text-xs uppercase tracking-mega text-gold">Packages</p>
          <h2 className="font-serif font-light text-4xl md:text-6xl text-white tracking-tight leading-[0.95]">
            Detailing for every deck
          </h2>
          <p className="mt-6 text-base md:text-lg font-light text-white/60 leading-relaxed">
            Priced per foot, like the pros — so you only pay for the boat you have. We're not the
            cheapest on the lake, and we're not trying to be: this is high-quality, hand-worked
            detailing that protects your investment. Every job starts with a free assessment.
          </p>
        </motion.div>

        <Estimator
          len={len}
          setLen={setLen}
          styleId={styleId}
          setStyleId={setStyleId}
          addons={addons}
          toggleAddon={toggleAddon}
          onBook={() => onQuote(buildPrefill(null))}
        />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {PRICING.map((tier, i) => (
            <motion.div
              key={tier.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: i * 0.1 }}
              className={`relative flex flex-col border p-8 md:p-10 ${
                tier.popular ? "border-gold bg-gold/[0.04]" : "border-white/10 bg-white/[0.02]"
              }`}
              data-testid={`pricing-tier-${tier.id}`}
            >
              {tier.popular && (
                <span className="absolute -top-3 left-8 bg-gold text-black text-[10px] uppercase tracking-[0.25em] px-3 py-1">
                  Most Popular
                </span>
              )}
              <p className="text-xs uppercase tracking-[0.25em] text-gold">{tier.tagline}</p>
              <h3 className="mt-3 font-serif text-3xl md:text-4xl text-white">{tier.name}</h3>
              <div className="mt-4 flex items-end gap-1.5">
                <span className="font-serif text-4xl text-white">{tier.price}</span>
                <span className="mb-1.5 text-sm text-white/50">{tier.unit}</span>
              </div>
              <p className="mt-1 text-[11px] uppercase tracking-[0.15em] text-gold/70">{tier.example}</p>
              <ul className="mt-8 space-y-3 flex-1">
                {tier.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-white/75">
                    <Check size={16} className="text-gold shrink-0 mt-1" />
                    <span className="text-sm">{f}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => onQuote(buildPrefill(tier.name))}
                className={`group mt-10 inline-flex items-center justify-center gap-2 px-8 py-4 text-xs uppercase tracking-[0.25em] transition-colors duration-300 ${
                  tier.popular
                    ? "bg-gold text-black hover:bg-gold-light"
                    : "border border-white/30 text-white hover:bg-white hover:text-black"
                }`}
                data-testid={`pricing-book-${tier.id}`}
              >
                Book this
                <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </button>
            </motion.div>
          ))}
        </div>

        {/* Membership teaser */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9 }}
          className="mt-6 relative overflow-hidden border border-gold/40 bg-gradient-to-br from-gold/[0.08] to-transparent p-10 md:p-14"
          data-testid="membership-teaser"
        >
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <p className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-gold">
                <Crown size={14} /> {MEMBERSHIP.kicker}
              </p>
              <h3 className="mt-4 font-serif font-light text-4xl md:text-5xl text-white">{MEMBERSHIP.name}</h3>
              <p className="mt-5 max-w-xl text-base font-light text-white/65 leading-relaxed">{MEMBERSHIP.desc}</p>
              <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3">
                {MEMBERSHIP.perks.map((p) => (
                  <span key={p} className="flex items-center gap-2 text-sm text-white/75">
                    <Check size={14} className="text-gold" /> {p}
                  </span>
                ))}
              </div>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <button
                onClick={() => navigate("/collective")}
                className="inline-flex items-center gap-2 border border-gold px-8 py-4 text-xs uppercase tracking-[0.25em] text-gold hover:bg-gold hover:text-black transition-colors duration-300"
                data-testid="membership-waitlist"
              >
                Discover the Collective
              </button>
              <p className="mt-3 text-[11px] text-white/30">The Founding Fleet — #001–#100.</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
